/**
 * Stripe customer tax ID field config, keyed by ISO 3166-1 alpha-2 country code.
 *
 * Generated from the Stripe supported-tax-ID table
 * (https://docs.stripe.com/tax/invoicing/tax-ids), cross-checked against
 * stripe/openapi spec 2026-06-24.dahlia. Placeholders are Stripe's official
 * example formats. Curated tier-1 subset (54 countries); full data
 * (all 116 types, multi-type countries) lives in tax-id-reference.full.json
 * alongside this file — reference material only, NOT imported.
 *
 * Countries with multiple Stripe types are pinned to one primary type here;
 * see inline comments for the alternatives and rationale. Kept in sync with
 * COUNTRY_TO_TAX_ID_TYPE in service-billing/service_billing/core/utils/tax_id.py.
 */

export interface TaxIdFieldConfig {
  label: string
  placeholder: string
  type: string // Stripe tax_id type enum value
}

export const TAX_ID_BY_COUNTRY: Record<string, TaxIdFieldConfig> = {
  // EU member states — all map to eu_vat for cross-border invoicing
  AT: { label: 'VAT number', placeholder: 'ATU12345678', type: 'eu_vat' },
  BE: { label: 'VAT number', placeholder: 'BE0123456789', type: 'eu_vat' },
  BG: { label: 'VAT number', placeholder: 'BG0123456789', type: 'eu_vat' },
  CY: { label: 'VAT number', placeholder: 'CY12345678Z', type: 'eu_vat' },
  CZ: { label: 'VAT number', placeholder: 'CZ1234567890', type: 'eu_vat' },
  DE: { label: 'VAT number', placeholder: 'DE123456789', type: 'eu_vat' },
  DK: { label: 'VAT number', placeholder: 'DK12345678', type: 'eu_vat' },
  EE: { label: 'VAT number', placeholder: 'EE123456789', type: 'eu_vat' },
  ES: { label: 'VAT number', placeholder: 'ESA1234567Z', type: 'eu_vat' },
  FI: { label: 'VAT number', placeholder: 'FI12345678', type: 'eu_vat' },
  FR: { label: 'VAT number', placeholder: 'FRAB123456789', type: 'eu_vat' },
  GR: { label: 'VAT number', placeholder: 'EL123456789', type: 'eu_vat' },
  HR: { label: 'VAT number', placeholder: 'HR12345678912', type: 'eu_vat' },
  HU: { label: 'VAT number', placeholder: 'HU12345678', type: 'eu_vat' },
  IE: { label: 'VAT number', placeholder: 'IE1234567AB', type: 'eu_vat' },
  IT: { label: 'VAT number', placeholder: 'IT12345678912', type: 'eu_vat' },
  LT: { label: 'VAT number', placeholder: 'LT123456789123', type: 'eu_vat' },
  LU: { label: 'VAT number', placeholder: 'LU12345678', type: 'eu_vat' },
  LV: { label: 'VAT number', placeholder: 'LV12345678912', type: 'eu_vat' },
  MT: { label: 'VAT number', placeholder: 'MT12345678', type: 'eu_vat' },
  NL: { label: 'VAT number', placeholder: 'NL123456789B12', type: 'eu_vat' },
  PL: { label: 'VAT number', placeholder: 'PL1234567890', type: 'eu_vat' },
  PT: { label: 'VAT number', placeholder: 'PT123456789', type: 'eu_vat' },
  RO: { label: 'VAT number', placeholder: 'RO1234567891', type: 'eu_vat' },
  SE: { label: 'VAT number', placeholder: 'SE123456789123', type: 'eu_vat' },
  SI: { label: 'VAT number', placeholder: 'SI12345678', type: 'eu_vat' },
  SK: { label: 'VAT number', placeholder: 'SK1234567891', type: 'eu_vat' },
  // Northern Ireland businesses use eu_vat with XI prefix — not modeled here
  GB: { label: 'VAT number', placeholder: 'GB123456789', type: 'gb_vat' },
  // ch_uid also exists; ch_vat affects Stripe Tax calc
  CH: { label: 'VAT number (MWST)', placeholder: 'CHE-123.456.789 MWST', type: 'ch_vat' },
  // no_voec exists for VOEC-registered e-commerce sellers
  NO: { label: 'VAT number (MVA)', placeholder: '123456789MVA', type: 'no_vat' },
  IS: { label: 'VAT number', placeholder: '123456', type: 'is_vat' },
  US: { label: 'EIN', placeholder: '12-3456789', type: 'us_ein' },
  // Per L3 decision. ca_qst / ca_pst_* exist for provincial numbers
  CA: { label: 'GST/HST number', placeholder: '123456789RT0002', type: 'ca_gst_hst' },
  MX: { label: 'RFC', placeholder: 'ABC010203AB9', type: 'mx_rfc' },
  // br_cpf is the personal ID; CNPJ chosen since field collects business tax IDs
  BR: { label: 'CNPJ', placeholder: '01.234.456/5432-10', type: 'br_cnpj' },
  // au_arn exists but is an ATO reference number, not a business identifier
  AU: { label: 'ABN', placeholder: '12345678912', type: 'au_abn' },
  NZ: { label: 'GST number', placeholder: '123456789', type: 'nz_gst' },
  // jp_trn (invoice-system T-number) — the number JP businesses need on invoices
  // post-2023 qualified-invoice rules, so their B2B customers can claim
  // consumption-tax credit. Stripe flags it as the only JP type that affects tax
  // calc. jp_cn (corporate number) and jp_rn (foreign-business ref) both exist but
  // aren't the right default: our JP customer base is enterprise-tier (essentially
  // all registered qualified-invoice issuers). Small tax-exempt businesses
  // (<¥10M revenue) won't have a T-number and see the field as unfillable —
  // accepted trade-off. See the Task Breakdown page's JP PO decision section.
  JP: { label: '登録番号 (Registration number)', placeholder: 'T1234567891234', type: 'jp_trn' },
  KR: { label: 'Business registration number', placeholder: '123-45-67890', type: 'kr_brn' },
  TW: { label: 'VAT number (統一編號)', placeholder: '12345678', type: 'tw_vat' },
  HK: { label: 'BR number', placeholder: '12345678', type: 'hk_br' },
  CN: { label: 'Tax ID (税号)', placeholder: '123456789012345678', type: 'cn_tin' },
  // sg_uen also exists; GST number is what appears on tax invoices
  SG: { label: 'GST registration number', placeholder: 'M12345678X', type: 'sg_gst' },
  // my_frp / my_itn also exist; SST is the sales & service tax registration
  MY: { label: 'SST number', placeholder: 'A12-3456-78912345', type: 'my_sst' },
  TH: {
    label: 'VAT number (เลขประจำตัวผู้เสียภาษี)',
    placeholder: '1234567891234',
    type: 'th_vat',
  },
  ID: { label: 'NPWP', placeholder: '012.345.678.9-012.345', type: 'id_npwp' },
  PH: { label: 'TIN', placeholder: '123456789012', type: 'ph_tin' },
  VN: { label: 'Tax ID (Mã số thuế)', placeholder: '1234567890', type: 'vn_tin' },
  IN: { label: 'GSTIN', placeholder: '12ABCDE3456FGZH', type: 'in_gst' },
  AE: { label: 'TRN', placeholder: '123456789012345', type: 'ae_trn' },
  SA: { label: 'VAT number', placeholder: '123456789012345', type: 'sa_vat' },
  IL: { label: 'VAT number', placeholder: '000012345', type: 'il_vat' },
  TR: { label: 'Tax ID (VKN)', placeholder: '0123456789', type: 'tr_tin' },
  ZA: { label: 'VAT number', placeholder: '4123456789', type: 'za_vat' },
}

export function getTaxIdMetaForCountry(countryCode?: string | null): TaxIdFieldConfig | null {
  if (!countryCode) {
    return null
  }
  return TAX_ID_BY_COUNTRY[countryCode.toUpperCase()] ?? null
}

interface TaxIdSaveError {
  response?: { status?: number }
}

export function getTaxIdErrorMessage(
  error: TaxIdSaveError | null | undefined,
  meta: TaxIdFieldConfig | null
): string {
  const httpStatus = error?.response?.status
  const isBadRequest = httpStatus !== undefined && httpStatus >= 400 && httpStatus < 500
  if (!isBadRequest) {
    return 'Could not save Tax ID — please try again or contact support.'
  }
  if (meta?.label && meta?.placeholder) {
    return `Please enter a valid ${meta.label} (e.g. ${meta.placeholder}).`
  }
  return 'Please enter a valid Tax ID.'
}



// WEBPACK FOOTER //
// ./src/libs/tax-id-by-country/index.ts